<script setup>
import { ref, onMounted } from 'vue';
import { useApi } from '@/utils/axios';
import { REVENDA_ROTAS } from '@/utils/apiRoutes';
import { ptBrTableLocale } from '@/utils/configuracoesTabela';

// Dados reativos para a tabela e estado da view
const historicoMapas = ref([]);
const loading = ref(false);
const error = ref(null);
const pagination = ref({
  page: 1,
  itemsPerPage: 15,
  totalItems: 0,
  totalPages: 0,
});

// Headers para a tabela
const headers = [
  { title: 'Nº da Revenda', key: 'revenda_id', sortable: true },
  { title: 'Revenda', key: 'nome_revenda', sortable: true },
  { title: 'Data de Alteração', key: 'data_atualizacao', sortable: true },
  { title: 'Data Original', key: 'data_original', sortable: true },
  { title: 'Atendente', key: 'nome_atendente', sortable: true },
];

// Função para buscar os dados da API
const fetchHistoricoMapas = async () => {
  loading.value = true;
  error.value = null;
  
  try {
    const api = useApi(REVENDA_ROTAS.HISTORICO_POLIGONOS);
    const params = {
      page: pagination.value.page,
      per_page: pagination.value.itemsPerPage
    };
    
    const response = await api.fetch({ params });
    
    if (response && response.status === 'success') {
      historicoMapas.value = response.data || [];
      
      if (response.meta) {
        pagination.value = {
          page: response.meta.current_page,
          itemsPerPage: response.meta.per_page,
          totalItems: response.meta.total,
          totalPages: response.meta.last_page
        };
      }
    } else {
      throw new Error(response?.message || 'Erro ao buscar histórico de mapas');
    }
  } catch (err) {
    console.error('Erro ao carregar histórico de mapas:', err);
    error.value = err.message || 'Erro ao carregar o histórico de mapas das revendas';
  } finally {
    loading.value = false;
  }
};

// Função para lidar com mudanças nas opções da tabela (paginação)
const handleDataTableOptionsChange = (options) => {
  // Check if the page has changed
  if (options.page !== pagination.value.page) {
    pagination.value.page = options.page;
    fetchHistoricoMapas();
    // Rola para o topo da página após carregar os novos dados
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }
};

// Função para criar uma chave única para cada item da tabela
const getItemKey = (item) => {
  // Combine revenda_id, data_atualizacao, and data_original to create a unique key
  return `${item.revenda_id}_${item.data_atualizacao.replace(/\D/g, '')}_${item.data_original.replace(/\D/g, '')}`;
};

// Carrega os dados quando o componente é montado
onMounted(() => {
  fetchHistoricoMapas();
});
</script>

<template>
  <div>
    <div class="d-flex align-center">
      <h1 class="text-h4">Histórico de Edições de Mapas das Revendas</h1>
      <v-spacer></v-spacer>
    </div>

    <v-card>
      <v-card-text>
        <v-alert 
          v-if="error" 
          type="error" 
          variant="tonal" 
          closable 
          class="mb-4"
        >
          {{ error }}
        </v-alert>

        <!-- Updated data-table with appropriate props for server-side pagination -->
        <v-data-table
          :headers="headers"
          :items="historicoMapas"
          :loading="loading"
          :page="pagination.page"
          :items-per-page="pagination.itemsPerPage"
          :server-items-length="pagination.totalItems"
          :item-key="getItemKey"
          :items-per-page-options="[15]"
          must-sort
          @update:options="handleDataTableOptionsChange"
          loading-text="Carregando histórico de mapas..."
          no-data-text="Nenhum registro encontrado"
          hover
          class="elevation-1"
        >
          <!-- Template para célula vazia (nome do atendente) -->
          <template v-slot:item.nome_atendente="{ item }">
            {{ item.nome_atendente || 'Não informado' }}
          </template>
          
          <!-- Template para ID da revenda com link para detalhes -->
          <template v-slot:item.revenda_id="{ item }">
            <router-link 
              :to="{ name: 'revenda-detalhes', params: { id: item.revenda_id }}" 
              class="text-decoration-none"
            >
              {{ item.revenda_id }}
            </router-link>
          </template>
          
          <!-- Footer template to show custom pagination info -->
          <template v-slot:bottom>
            <div class="d-flex justify-space-between align-center pa-2">
              <div class="text-caption">
                Mostrando {{ historicoMapas.length }} de {{ pagination.totalItems }} registros
              </div>
              
              <div class="d-flex align-center">
                <!-- First page button -->
                <v-btn
                  icon="mdi-page-first"
                  size="small"
                  variant="text"
                  :disabled="pagination.page === 1 || loading"
                  @click="pagination.page = 1; fetchHistoricoMapas();"
                ></v-btn>
                
                <!-- Previous page button -->
                <v-btn
                  icon="mdi-chevron-left"
                  size="small"
                  variant="text"
                  :disabled="pagination.page === 1 || loading"
                  @click="pagination.page--; fetchHistoricoMapas();"
                  class="mx-2"
                ></v-btn>
                
                <!-- Page info text -->
                <span class="text-caption mx-2">
                  Página {{ pagination.page }} de {{ pagination.totalPages }}
                </span>
                
                <!-- Next page button -->
                <v-btn
                  icon="mdi-chevron-right"
                  size="small"
                  variant="text"
                  :disabled="pagination.page === pagination.totalPages || loading"
                  @click="pagination.page++; fetchHistoricoMapas();"
                  class="mx-2"
                ></v-btn>
                
                <!-- Last page button -->
                <v-btn
                  icon="mdi-page-last"
                  size="small"
                  variant="text"
                  :disabled="pagination.page === pagination.totalPages || loading"
                  @click="pagination.page = pagination.totalPages; fetchHistoricoMapas();"
                ></v-btn>
              </div>
            </div>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>
  </div>
</template>

<style scoped>
/* Mantém estilos consistentes com o restante da aplicação */
</style>
